import { createFileRoute, redirect, useLocation, useNavigate, Outlet } from '@tanstack/react-router';
import { useUser } from '@clerk/clerk-react';
import { useEffect } from 'react';
import { SubscriptionGuard } from '../components/SubscriptionGuard';
import { useTranslation } from 'react-i18next';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

// Helper function to wait for Clerk to load
const waitForClerk = async (maxAttempts = 50) => {
  for (let i = 0; i < maxAttempts; i++) {
    if (window.Clerk?.user !== undefined) {
      return window.Clerk.user;
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  return window.Clerk?.user;
};

export const Route = createFileRoute('/admin')({
  component: AdminPage,
  // Admin-specific auth check
  beforeLoad: async ({}) => {
    // Wait for Clerk to load
    const auth = await waitForClerk();
    if (!auth) {
      return redirect({
        to: '/',
        replace: true,
      });
    }

    // Check if user is an admin using the same logic as our useIsAdmin hook
    // This requires that Clerk JWT template includes the role from publicMetadata
    const userRole = ((auth as any).publicMetadata as { role?: string })?.role;
    if (userRole !== 'admin') {
      // If user is logged in but not admin, redirect to dashboard
      return redirect({
        to: '/dashboard',
        replace: true,
      });
    }

    // Allow access for admins
  },
});

function AdminPage() {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // Get current tab from the pathname
  const getCurrentTab = () => {
    const pathname = location.pathname;
    if (pathname.includes('/messages')) return 'messages';
    if (pathname.includes('/users')) return 'users';
    if (pathname.includes('/chat')) return 'chat';
    if (pathname.includes('/push-notifications')) return 'push-notifications';
    if (pathname.includes('/settings')) return 'settings';
    if (pathname.includes('/reports')) return 'reports';
    if (pathname.includes('/connectors')) return 'connectors';
    if (pathname.includes('/social-posts')) return 'social-posts';
    return 'messages';
  };

  const activeTab = getCurrentTab();

  // Redirect to messages tab if visiting /admin directly
  useEffect(() => {
    if (location.pathname === '/admin') {
      navigate({ to: '/admin/messages', replace: true });
    }
  }, [location.pathname, navigate]);

  // Log admin access in development only
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Admin page accessed by:', user?.id);
    }
  }, [user]);

  return (
    <SubscriptionGuard>
      <div className='p-4'>
        <h1 className='text-2xl font-bold mb-6'>{t('admin.title', 'Admin')}</h1>

        <div className='bg-white shadow-sm rounded-lg p-6'>
          <p className='text-gray-500 mb-4'>
            {t('admin.description', 'Admin panel for system management')}
          </p>

          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              navigate({ to: `/admin/${value}` });
            }}
            className='w-full mt-4'
          >
            <TabsList className='grid w-full grid-cols-8'>
              <TabsTrigger value='messages'>
                {t('admin.messagesTab', 'Messages')}
              </TabsTrigger>
              <TabsTrigger value='users'>
                {t('admin.users', 'Users')}
              </TabsTrigger>
              <TabsTrigger value='chat'>
                {t('admin.chatTab', 'Chat')}
              </TabsTrigger>
              <TabsTrigger value='push-notifications'>
                Push Notifications
              </TabsTrigger>
              <TabsTrigger value='settings'>
                {t('admin.settingsTab', 'Settings')}
              </TabsTrigger>
              <TabsTrigger value='reports'>Reports</TabsTrigger>
              <TabsTrigger value='connectors'>Connectors</TabsTrigger>
              <TabsTrigger value='social-posts'>Social Posts</TabsTrigger>
            </TabsList>

            <div className='mt-6'>
              <Outlet />
            </div>
          </Tabs>
        </div>
      </div>
    </SubscriptionGuard>
  );
}
